(function (root) {
  'use strict';

  function normalizeZip(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 5);
  }

  function isValidZip(value) {
    return /^\d{5}$/.test(String(value || ''));
  }

  function distanceMiles(origin, destination) {
    var earthRadiusMiles = 3958.8;
    var toRadians = function (degrees) { return degrees * Math.PI / 180; };
    var latDifference = toRadians(destination.latitude - origin.latitude);
    var lngDifference = toRadians(destination.longitude - origin.longitude);
    var a = Math.sin(latDifference / 2) * Math.sin(latDifference / 2) +
      Math.cos(toRadians(origin.latitude)) * Math.cos(toRadians(destination.latitude)) *
      Math.sin(lngDifference / 2) * Math.sin(lngDifference / 2);
    return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function findMatches(retailers, zip, origin, radius) {
    return retailers.filter(function (retailer) {
      if (retailer.zip === zip) return true;
      if (!origin || !Number.isFinite(retailer.latitude) || !Number.isFinite(retailer.longitude)) return false;
      return distanceMiles(origin, retailer) <= radius;
    });
  }

  root.AridsRetailerFinderUtils = {
    normalizeZip: normalizeZip,
    isValidZip: isValidZip,
    distanceMiles: distanceMiles,
    findMatches: findMatches
  };

  if (!root.document) return;

  var googleMapsPromise;

  function loadGoogleMaps(apiKey) {
    if (root.google && root.google.maps) return Promise.resolve(root.google.maps);
    if (!apiKey) return Promise.reject(new Error('Google Maps key is not configured.'));
    if (googleMapsPromise) return googleMapsPromise;

    googleMapsPromise = new Promise(function (resolve, reject) {
      var callbackName = '__aridsRetailerMapsReady';
      var script = root.document.createElement('script');
      root[callbackName] = function () {
        delete root[callbackName];
        resolve(root.google.maps);
      };
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&callback=' + callbackName;
      script.async = true;
      script.defer = true;
      script.onerror = function () {
        delete root[callbackName];
        reject(new Error('Google Maps failed to load.'));
      };
      root.document.head.appendChild(script);
    });

    return googleMapsPromise;
  }

  function geocodeZip(maps, zip) {
    return new Promise(function (resolve, reject) {
      var geocoder = new maps.Geocoder();
      geocoder.geocode({address: zip, componentRestrictions: {country: 'US'}}, function (results, status) {
        if (status !== 'OK' || !results[0]) {
          reject(new Error('ZIP code could not be geocoded.'));
          return;
        }
        resolve({
          latitude: results[0].geometry.location.lat(),
          longitude: results[0].geometry.location.lng()
        });
      });
    });
  }

  function parseRetailers(section) {
    return Array.prototype.map.call(section.querySelectorAll('[data-retailer-card]'), function (card) {
      return {
        card: card,
        name: card.dataset.name || '',
        address: card.dataset.address || '',
        zip: normalizeZip(card.dataset.zip),
        latitude: parseFloat(card.dataset.latitude),
        longitude: parseFloat(card.dataset.longitude),
        marker: null
      };
    });
  }

  function updateResults(retailers, matches, emptyState, status, searchedZip) {
    var matchSet = new Set(matches);
    retailers.forEach(function (retailer) {
      var isMatch = matchSet.has(retailer);
      retailer.card.hidden = !isMatch;
    });
    emptyState.classList.toggle('is-hidden', matches.length > 0);
    if (matches.length > 0) {
      status.textContent = matches.length + (matches.length === 1 ? ' retailer found' : ' retailers found') + ' for ' + searchedZip + '.';
      status.classList.remove('is-error');
    } else {
      status.textContent = status.dataset.noResultsMessage;
      status.classList.remove('is-error');
    }
  }

  function initializeMap(section, retailers) {
    var mapElement = section.querySelector('[data-retailer-map]');
    var fallback = section.querySelector('[data-map-fallback]');
    var leaflet = root.L;
    if (!mapElement || !leaflet) return null;
    var mappableRetailers = retailers.filter(function (retailer) {
      return Number.isFinite(retailer.latitude) && Number.isFinite(retailer.longitude);
    });
    if (!mappableRetailers.length) return null;

    mapElement.replaceChildren();
    var map = leaflet.map(mapElement, {
      scrollWheelZoom: true,
      zoomControl: true
    });
    leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(map);

    var bounds = leaflet.latLngBounds([]);
    var markerIcon = leaflet.divIcon({
      className: 'retailer-map-marker-shell',
      html: '<span class="retailer-map-marker" aria-hidden="true"></span>',
      iconAnchor: [12, 24],
      iconSize: [24, 24],
      popupAnchor: [0, -24]
    });

    mappableRetailers.forEach(function (retailer) {
      var position = [retailer.latitude, retailer.longitude];
      var content = root.document.createElement('div');
      var title = root.document.createElement('strong');
      var address = root.document.createElement('div');
      var directions = root.document.createElement('a');
      title.textContent = retailer.name;
      address.textContent = retailer.address;
      directions.textContent = 'Directions';
      directions.href = 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(retailer.address);
      directions.target = '_blank';
      directions.rel = 'noopener';
      content.appendChild(title);
      content.appendChild(address);
      content.appendChild(directions);

      var marker = leaflet.marker(position, {
        alt: retailer.name,
        icon: markerIcon,
        keyboard: true,
        title: retailer.name
      }).addTo(map).bindPopup(content);
      retailer.marker = marker;
      bounds.extend(position);
    });

    if (mappableRetailers.length === 1) {
      map.setView([mappableRetailers[0].latitude, mappableRetailers[0].longitude], 11);
    } else {
      map.fitBounds(bounds, {maxZoom: 11, padding: [28, 28]});
    }
    root.setTimeout(function () { map.invalidateSize(false); }, 0);
    if (fallback) fallback.hidden = true;
    return map;
  }

  function focusMap(map, retailers) {
    if (!map) return;
    var mappableRetailers = retailers.filter(function (retailer) {
      return retailer.marker && Number.isFinite(retailer.latitude) && Number.isFinite(retailer.longitude);
    });
    if (!mappableRetailers.length) return;

    if (mappableRetailers.length === 1) {
      var retailer = mappableRetailers[0];
      map.setView([retailer.latitude, retailer.longitude], 13);
      retailer.marker.openPopup();
      return;
    }

    var leaflet = root.L;
    var bounds = leaflet.latLngBounds(mappableRetailers.map(function (retailer) {
      return [retailer.latitude, retailer.longitude];
    }));
    map.fitBounds(bounds, {maxZoom: 12, padding: [28, 28]});
  }

  function initializeSection(section) {
    if (section.dataset.retailerFinderInitialized === 'true') return;
    section.dataset.retailerFinderInitialized = 'true';

    var retailers = parseRetailers(section);
    var emptyState = section.querySelector('[data-retailer-empty]');
    var resultsDetails = section.querySelector('[data-retailer-results]');
    var requestLink = section.querySelector('[data-request-retailer-link]');
    var requestForm = section.querySelector('[data-retailer-request-form]');
    var mapApiKey = section.dataset.mapApiKey || '';
    var radius = parseInt(section.dataset.searchRadius, 10) || 50;
    var map = initializeMap(section, retailers);
    var geocoderReady = null;

    if (mapApiKey) {
      geocoderReady = loadGoogleMaps(mapApiKey).catch(function () { return null; });
    }

    if (map) {
      var resizeTimer;
      root.addEventListener('resize', function () {
        root.clearTimeout(resizeTimer);
        resizeTimer = root.setTimeout(function () { map.invalidateSize(false); }, 120);
      });
    }

    var searchForm = section.querySelector('[data-retailer-search]');
    if (searchForm) {
      var zipInput = searchForm.querySelector('[data-retailer-zip]');
      var status = section.querySelector('[data-retailer-status]');
      zipInput.addEventListener('input', function () {
        zipInput.value = normalizeZip(zipInput.value);
        zipInput.removeAttribute('aria-invalid');
        status.textContent = '';
        status.classList.remove('is-error');
      });

      searchForm.addEventListener('submit', function (event) {
        event.preventDefault();
        var zip = normalizeZip(zipInput.value);
        if (!isValidZip(zip)) {
          zipInput.setAttribute('aria-invalid', 'true');
          status.textContent = status.dataset.invalidMessage;
          status.classList.add('is-error');
          zipInput.focus();
          return;
        }

        zipInput.removeAttribute('aria-invalid');
        if (resultsDetails) resultsDetails.open = true;
        var exactMatches = findMatches(retailers, zip, null, radius);
        if (!geocoderReady) {
          updateResults(retailers, exactMatches, emptyState, status, zip);
          focusMap(map, exactMatches);
          return;
        }

        status.textContent = 'Searching for nearby retailers…';
        geocoderReady.then(function (maps) {
          if (!maps) return null;
          return geocodeZip(maps, zip);
        }).then(function (origin) {
          var matches = origin ? findMatches(retailers, zip, origin, radius) : exactMatches;
          updateResults(retailers, matches, emptyState, status, zip);
          focusMap(map, matches);
        }).catch(function () {
          updateResults(retailers, exactMatches, emptyState, status, zip);
          focusMap(map, exactMatches);
        });
      });
    }

    if (requestLink) {
      requestLink.addEventListener('click', function () {
        var target = section.querySelector(requestLink.getAttribute('href'));
        if (target) root.setTimeout(function () { target.focus(); }, 0);
      });
    }

    if (requestForm) {
      var submitting = false;
      var submitButton = requestForm.querySelector('[data-retailer-request-submit]');
      requestForm.addEventListener('submit', function (event) {
        if (submitting) {
          event.preventDefault();
          return;
        }
        if (!requestForm.checkValidity()) return;
        submitting = true;
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.textContent = 'Sending…';
        }
      });
    }

    var errors = section.querySelector('[data-form-errors]');
    if (errors) errors.focus();
  }

  function initializeAll(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-retailer-finder]'), initializeSection);
  }

  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', function () { initializeAll(root.document); });
  } else {
    initializeAll(root.document);
  }

  root.document.addEventListener('shopify:section:load', function (event) { initializeAll(event.target); });
})(typeof window !== 'undefined' ? window : this);